import React, { useState, useEffect } from "react";
import API from "../services/api";
import DateRangeFilter from "../components/DateRangeFilter";

const PaymentReconciliation = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [dateParams, setDateParams] = useState(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return {
      startDate: startOfMonth.toISOString(),
      endDate: endOfDay.toISOString(),
    };
  });

  const fetchReconciliationReport = async (params = dateParams) => {
    try {
      setLoading(true);
      setError("");
      const res = await API.get("/reconciliation", { params });
      setData(res.data?.data || res.data);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load payment reconciliation data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReconciliationReport(dateParams);
  }, [dateParams]);

  const handleDateFilterChange = (newRange) => {
    if (newRange.startDate && newRange.endDate) {
      setDateParams(newRange);
    }
  };

  const openDetailsModal = (txn) => {
    setSelectedTxn(txn);
    setIsModalOpen(true);
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case "MATCHED":
        return <span style={{ ...styles.badge, background: "#dcfce7", color: "#15803d" }}>✓ MATCHED</span>;
      case "PAYMENT_NOT_REFLECTED":
        return <span style={{ ...styles.badge, background: "#fef3c7", color: "#b45309" }}>⚠ NOT REFLECTED</span>;
      case "REFUND_NOT_REFLECTED":
        return <span style={{ ...styles.badge, background: "#fee2e2", color: "#991b1b" }}>⚠️ REFUND UNLINKED</span>;
      case "REFUND_IN_PROGRESS":
        return <span style={{ ...styles.badge, background: "#e0f2fe", color: "#0369a1" }}>⏳ REFUND PENDING</span>;
      case "ORPHANED_PAYMENT":
      case "NO_BOOKING_LINKED":
        return <span style={{ ...styles.badge, background: "#fee2e2", color: "#b91c1c" }}>❌ ORPHANED</span>;
      case "AMOUNT_MISMATCH":
        return <span style={{ ...styles.badge, background: "#f3e8ff", color: "#6b21a8" }}>≠ MISMATCH</span>;
      default:
        return <span style={{ ...styles.badge, background: "#f1f5f9", color: "#475569" }}>{status}</span>;
    }
  };

  if (loading && !data) {
    return (
      <div style={styles.centerScreen}>
        <div style={styles.loader}></div>
        <p>Syncing Reconciliation Report...</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Payment Reconciliation</h2>
          <p style={styles.subtitle}>Audit Razorpay gateway payments against database records</p>
        </div>
        <button style={styles.refreshBtn} onClick={() => fetchReconciliationReport(dateParams)}>
          ↻ Refresh Audit
        </button>
      </div>

      <DateRangeFilter onChange={handleDateFilterChange} />

      {error && (
        <div style={styles.errorState}>
          <h3>⚠️ Reconciliation Sync Error</h3>
          <p>{error}</p>
        </div>
      )}

      {data && (
        <>
          <div style={styles.summaryGrid}>
            <div style={{ ...styles.summaryCard, borderLeft: "4px solid #2563eb" }}>
              <span style={styles.summaryLabel}>TOTAL CHECKED</span>
              <span style={styles.summaryValue}>{data.totalPaymentsChecked ?? 0}</span>
              <span style={styles.summarySubText}>Razorpay Transactions</span>
            </div>
            <div style={{ ...styles.summaryCard, borderLeft: "4px solid #16a34a" }}>
              <span style={styles.summaryLabel}>MATCHED TRANSACTIONS</span>
              <span style={{ ...styles.summaryValue, color: "#16a34a" }}>{data.matchedCount ?? 0}</span>
              <span style={styles.summarySubText}>Verified & Correct</span>
            </div>
            <div style={{ ...styles.summaryCard, borderLeft: "4px solid #dc2626" }}>
              <span style={styles.summaryLabel}>DISCREPANCIES / ISSUES</span>
              <span style={{ ...styles.summaryValue, color: "#dc2626" }}>{data.issueCount ?? 0}</span>
              <span style={styles.summarySubText}>Requires Manual Check</span>
            </div>
          </div>

          <div style={styles.tableCard}>
            <div style={styles.tableHeaderBar}>
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700" }}>Transaction Audit Log</h3>
              <span style={{ fontSize: "12px", color: "#64748b" }}>
                Showing {data.transactions?.length || 0} records
              </span>
            </div>

            {(!data.transactions || data.transactions.length === 0) ? (
              <div style={styles.emptyCard}>No payment records found for the selected date range.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeadRow}>
                      <th style={styles.th}>PAYMENT ID</th>
                      <th style={styles.th}>RAZORPAY GATEWAY</th>
                      <th style={styles.th}>DATABASE BOOKING</th>
                      <th style={styles.th}>STATUS</th>
                      <th style={styles.th}>AUDIT DETAIL</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((row, idx) => (
                      <tr key={idx} style={styles.tableRow}>
                        <td style={styles.td}>
                          <span style={styles.codeText}>{row.razorpay?.paymentId || "N/A"}</span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: "700", color: "#0f172a" }}>
                            ₹{row.razorpay?.amount || 0}
                          </div>
                          {row.razorpay?.amountRefunded > 0 && (
                            <div style={{ fontSize: "11px", color: "#b91c1c", fontWeight: "600" }}>
                              Refunded: ₹{row.razorpay.amountRefunded} ({row.razorpay.refundState})
                            </div>
                          )}
                          <div style={styles.subText}>
                            {row.razorpay?.method} • {row.razorpay?.createdAt ? new Date(row.razorpay.createdAt).toLocaleString() : "N/A"}
                          </div>
                        </td>
                        <td style={styles.td}>
                          {row.system ? (
                            <div>
                              <div style={{ fontWeight: "600" }}>
                                {row.system.customerName || "Customer"} ({row.system.customerPhone || "N/A"})
                              </div>
                              <div style={styles.subText}>
                                DB Paid: ₹{row.system.amountPaid || 0} | Status: <strong>{row.system.paymentStatus}</strong>
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No linked booking</span>
                          )}
                        </td>
                        <td style={styles.td}>{renderStatusBadge(row.matchStatus)}</td>
                        <td style={{ ...styles.td, maxWidth: "260px" }}>
                          <span style={{ fontSize: "12px", color: "#475569" }}>{row.detail}</span>
                        </td>
                        <td style={{ ...styles.td, textAlign: "right" }}>
                          <button style={styles.viewBtn} onClick={() => openDetailsModal(row)}>
                            Details →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {isModalOpen && selectedTxn && (
        <div style={styles.overlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px" }}>Audit Breakdown</h3>
                <span style={styles.modalSubId}>Payment ID: {selectedTxn.razorpay?.paymentId}</span>
              </div>
              <button style={styles.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.statusBox}>
                <span>RECONCILIATION RESULT:</span>
                {renderStatusBadge(selectedTxn.matchStatus)}
              </div>

              <section style={styles.section}>
                <h4 style={styles.sectionTitle}>RAZORPAY GATEWAY RECORD</h4>
                <div style={styles.detailRow}><span>Payment ID</span> <strong>{selectedTxn.razorpay?.paymentId}</strong></div>
                <div style={styles.detailRow}><span>Captured Amount</span> <strong style={{ color: "#2563eb" }}>₹{selectedTxn.razorpay?.amount}</strong></div>
                <div style={styles.detailRow}><span>Amount Refunded</span> <strong style={{ color: selectedTxn.razorpay?.amountRefunded > 0 ? "#b91c1c" : "#0f172a" }}>₹{selectedTxn.razorpay?.amountRefunded}</strong></div>
                <div style={styles.detailRow}><span>Refund State</span> <strong>{selectedTxn.razorpay?.refundState}</strong></div>
                <div style={styles.detailRow}><span>Gateway Status</span> <strong>{selectedTxn.razorpay?.status}</strong></div>
                <div style={styles.detailRow}><span>Payment Method</span> <strong>{selectedTxn.razorpay?.method}</strong></div>
              </section>

              <section style={styles.section}>
                <h4 style={styles.sectionTitle}>SYSTEM DATABASE RECORD</h4>
                {selectedTxn.system ? (
                  <>
                    <div style={styles.detailRow}><span>Booking ID</span> <strong>#{selectedTxn.system.bookingId}</strong></div>
                    <div style={styles.detailRow}><span>Customer</span> <strong>{selectedTxn.system.customerName} ({selectedTxn.system.customerPhone})</strong></div>
                    <div style={styles.detailRow}><span>Driver</span> <strong>{selectedTxn.system.driverName} ({selectedTxn.system.driverPhone})</strong></div>
                    <div style={styles.detailRow}><span>DB Paid Amount</span> <strong>₹{selectedTxn.system.amountPaid}</strong></div>
                    <div style={styles.detailRow}><span>DB Payment Status</span> <strong>{selectedTxn.system.paymentStatus}</strong></div>
                    <div style={styles.detailRow}><span>Refund ID in DB</span> <strong>{selectedTxn.system.refundId || "None"}</strong></div>
                  </>
                ) : (
                  <div style={styles.noDataBox}>⚠️ No corresponding booking record found in database.</div>
                )}
              </section>

              {selectedTxn.refundProcessingNote && (
                <div style={styles.refundNoteBox}>
                  <strong>Refund Status Note:</strong> {selectedTxn.refundProcessingNote}
                </div>
              )}

              <div style={styles.timestampBox}>
                <strong>Audit Finding:</strong> {selectedTxn.detail}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: { padding: "40px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif", color: "#1e293b" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
  title: { fontSize: "28px", fontWeight: "700", margin: 0 },
  subtitle: { fontSize: "14px", color: "#64748b" },
  
  refreshBtn: { padding: "10px 20px", background: "#fff", color: "#0f172a", border: "1px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", fontWeight: "600", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" },

  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px", marginBottom: "24px" },
  summaryCard: { background: "#fff", padding: "20px", borderRadius: "16px", border: "1px solid #e2e8f0", display: "flex", flexDirection: "column" },
  summaryLabel: { fontSize: "11px", fontWeight: "800", color: "#94a3b8", letterSpacing: "0.5px" },
  summaryValue: { fontSize: "30px", fontWeight: "800", margin: "6px 0", color: "#0f172a" },
  summarySubText: { fontSize: "12px", color: "#64748b" },

  tableCard: { background: "#fff", borderRadius: "20px", border: "1px solid #e2e8f0", overflow: "hidden" },
  tableHeaderBar: { padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" },
  table: { width: "100%", borderCollapse: "collapse", textAlign: "left" },
  tableHeadRow: { background: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  th: { padding: "14px 20px", fontSize: "11px", fontWeight: "800", color: "#64748b", letterSpacing: "0.5px" },
  tableRow: { borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" },
  td: { padding: "16px 20px", fontSize: "13px", verticalAlign: "middle" },
  
  codeText: { fontFamily: "monospace", fontSize: "12px", background: "#f1f5f9", padding: "3px 8px", borderRadius: "6px", fontWeight: "600", color: "#334155" },
  subText: { fontSize: "11px", color: "#94a3b8", marginTop: "3px" },
  badge: { padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "800", display: "inline-block" },
  
  viewBtn: { background: "none", border: "none", color: "#2563eb", fontWeight: "700", cursor: "pointer", fontSize: "12px" },

  overlay: { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.75)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", width: "520px", borderRadius: "24px", overflow: "hidden", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" },
  modalHeader: { padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalSubId: { fontSize: "12px", color: "#64748b" },
  closeBtn: { fontSize: "26px", border: "none", background: "none", cursor: "pointer", color: "#94a3b8" },
  
  modalBody: { padding: "24px", maxHeight: "80vh", overflowY: "auto" },
  statusBox: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#f8fafc", borderRadius: "12px", marginBottom: "20px", fontSize: "12px", fontWeight: "700", color: "#64748b" },
  section: { marginBottom: "20px" },
  sectionTitle: { fontSize: "11px", fontWeight: "800", color: "#94a3b8", marginBottom: "10px", letterSpacing: "0.5px" },
  detailRow: { display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "13px" },
  noDataBox: { padding: "12px", background: "#fef2f2", color: "#991b1b", borderRadius: "8px", fontSize: "12px", fontWeight: "500" },
  
  refundNoteBox: { marginBottom: "16px", padding: "12px", background: "#f0f9ff", border: "1px solid #bae6fd", color: "#0369a1", borderRadius: "8px", fontSize: "12px" },
  timestampBox: { marginTop: "16px", padding: "12px", background: "#f1f5f9", borderRadius: "8px", fontSize: "12px", color: "#475569", lineHeight: "1.5" },

  centerScreen: { height: "80vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", fontFamily: "'Inter', sans-serif" },
  loader: { width: "32px", height: "32px", border: "3px solid #f3f3f3", borderTop: "3px solid #2563eb", borderRadius: "50%", animation: "spin 1s linear infinite", marginBottom: "12px" },
  emptyCard: { textAlign: "center", padding: "60px", background: "#fff", color: "#94a3b8", fontSize: "14px" },
  errorState: { padding: "16px", background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: "12px", marginBottom: "20px" }
};

export default PaymentReconciliation;